import { ChevronDownIcon, SearchIcon } from "@heroicons/react/outline";
import { differenceInDays } from "date-fns";
import { useRouter } from "next/router";
import React, { useEffect, useRef } from "react";
import { UserFilled } from "ui/components/icons";
import { getValidDate } from "ui/lib/helpers";
import { cn } from "ui/lib/utils";
import { MemberInfoScreen } from "apollo/components/chat/user-info";
import { ActionMenu } from "apollo/components/mobile/action-menu";
import { AdminSectionLabel } from "apollo/components/mobile/admin-page";
import { BottomSheet } from "apollo/components/mobile/bottom-sheet";
import { ListRow } from "apollo/components/mobile/list-row";
import { LoadState } from "apollo/components/mobile/load-state";
import { PushedScreen } from "apollo/components/mobile/pushed-screen";
import { usePushedView } from "apollo/components/mobile/pushed-view";
import { useShellTeamId } from "apollo/lib/last-team";
import {
  CommandCenterMembersSearchProvider,
  SEARCH_TIMEOUT_MS,
  useCommandCenterMembersSearch,
} from "./containers/members/search";
import { SearchCoverage } from "./search-coverage";

/** `?searchSheet=open`, so Back closes the phone search. */
export const useSearchSheet = () => usePushedView("searchSheet");

/** `?member=<id>`: a search result's member info, pushed over the page. */
const useMemberView = () => usePushedView("member");

// Where the results list was scrolled to, per query, so Back from a member
// returns to the same row.
const scrollFor = new Map<string, number>();

// Accounts without a real address carry a hash in its place.
const isHashedEmail = (email?: string) =>
  !email || /^[a-f0-9]{32,}(@|$)/i.test(email);

const lastActive = (value?: string) => {
  if (!value) return "";
  const days = differenceInDays(new Date(), getValidDate(value));
  return days ? `active ${days}d ago` : "active today";
};

const Avatar = ({ url }: { url?: string }) =>
  url ? (
    <span
      aria-hidden="true"
      style={{ backgroundImage: `url(${url}?w=80)` }}
      className="block size-10 rounded-full bg-white/[0.06] bg-cover bg-center"
    />
  ) : (
    <span
      aria-hidden="true"
      className="flex size-10 items-center justify-center rounded-full bg-white/[0.06]"
    >
      <UserFilled className="w-4 text-white/60" />
    </span>
  );

type Member = {
  id: string;
  label: string;
  teamID?: string;
  bootcampName?: string;
  profileImg?: string;
  lastActivityTS?: string;
  email?: string;
};

const MemberRows = ({
  members,
  showTeam,
  onOpen,
}: {
  members: Member[];
  showTeam: boolean;
  onOpen: (id: string) => void;
}) => (
  <ul aria-label="Members" className="space-y-0.5 pb-2">
    {members.map((member) => (
      <li key={member.id}>
        <ListRow
          onClick={() => onOpen(member.id)}
          leading={<Avatar url={member.profileImg} />}
          title={member.label}
          subtitle={[
            showTeam ? member.bootcampName || "Onboarding" : null,
            lastActive(member.lastActivityTS) ||
              (isHashedEmail(member.email) ? null : member.email),
          ]
            .filter(Boolean)
            .join(" · ")}
          chevron
        />
      </li>
    ))}
  </ul>
);

const Results = ({
  inputRef,
}: {
  inputRef: React.RefObject<HTMLInputElement>;
}) => {
  const router = useRouter();
  const memberView = useMemberView();
  const shellTeamId = useShellTeamId();
  const {
    inputValue,
    setInputValue,
    filteredData,
    query,
    searching,
    searchingAll,
    partial,
    hasNextPage,
    fetchNextPage,
    isFetching,
    error,
    retry,
    teamID,
    setTeamID,
    bootcamps,
  } = useCommandCenterMembersSearch();
  const team = bootcamps?.find(({ id }) => id === teamID);
  const scrollerRef = useRef<HTMLDivElement>(null);

  // The query lives in the URL, so Back from a member brings it back (the
  // provider starts from it); after that the field leads and the URL follows.
  const urlQuery =
    typeof router.query.searchQ === "string" ? router.query.searchQ : "";
  const urlTeam =
    typeof router.query.searchTeam === "string" ? router.query.searchTeam : "";
  // Until the restored query reaches the search, the field's empty start must
  // not write itself over the URL's query.
  const restoring = useRef(!!urlQuery);
  useEffect(() => {
    if (restoring.current) {
      if (query === urlQuery) restoring.current = false;
      return;
    }
    if (!router.isReady || router.query.searchSheet !== "open") return;
    if (urlQuery === query && urlTeam === teamID) return;
    const { searchQ: _q, searchTeam: _team, ...rest } = router.query;
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...rest,
          ...(query ? { searchQ: query } : {}),
          ...(teamID ? { searchTeam: teamID } : {}),
        },
      },
      undefined,
      { shallow: true, scroll: false }
    );
  }, [query, teamID]);

  const scrollKey = `${teamID}|${query}`;
  // A few tries while the rows arrive, then the list is the coach's to scroll.
  const restoreTries = useRef(0);
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (restoreTries.current >= 5 || !scroller || !filteredData.length) return;
    restoreTries.current += 1;
    const top = scrollFor.get(scrollKey) ?? 0;
    scroller.scrollTop = top;
    if (scroller.scrollTop >= top - 1) restoreTries.current = 5;
  });

  const openMember = (id: string) => {
    if (scrollerRef.current)
      scrollFor.set(scrollKey, scrollerRef.current.scrollTop);
    // The search's entry keeps its query, so Back reopens it as it was.
    memberView.open(id, { drop: ["searchSheet"] });
  };

  // Across all teams, the team the coach is in comes first.
  const shellTeam = bootcamps?.find(({ id }) => id === shellTeamId);
  const grouped = !teamID && !!shellTeam;
  const mine = grouped
    ? (filteredData as Member[]).filter((m) => m.teamID === shellTeamId)
    : [];
  const others = grouped
    ? (filteredData as Member[]).filter((m) => m.teamID !== shellTeamId)
    : (filteredData as Member[]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-2 px-2 pb-3">
        <label className="relative flex min-w-0 flex-1 items-center">
          <span className="sr-only">Search members</span>
          <SearchIcon
            aria-hidden="true"
            className="pointer-events-none absolute left-3 size-5 text-faint"
          />
          <input
            ref={inputRef}
            type="search"
            enterKeyHint="search"
            autoComplete="off"
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            placeholder="Name, email or member ID"
            className="h-11 w-full rounded-xl bg-white/[0.06] pl-10 pr-3 text-[16px] text-white/90 placeholder:text-white/45 focus:outline-none focus:ring-2 focus:ring-primary/60"
          />
        </label>
        <ActionMenu
          title="Filter by team"
          options={[
            { value: "", label: "All teams", selected: !teamID },
            ...(bootcamps || []).map(({ id, name }) => ({
              value: id,
              label: name,
              selected: id === teamID,
            })),
          ]}
          onSelect={(value) => setTeamID(value)}
          trigger={
            <button
              type="button"
              aria-label={`Team: ${team?.name || "All teams"}. Change team`}
              className={cn(
                "flex h-11 max-w-[40%] shrink-0 items-center gap-1 rounded-full px-3.5 text-[14px] font-medium",
                team
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground"
              )}
            >
              <span className="truncate">{team?.name || "All teams"}</span>
              <ChevronDownIcon aria-hidden="true" className="size-4 shrink-0" />
            </button>
          }
        />
      </div>
      <div
        ref={scrollerRef}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
      >
        {!query ? (
          <p className="px-4 py-10 text-center text-[15px] text-white/60">
            Search members by name, email or member ID.
          </p>
        ) : (
          <LoadState
            loading={searching || (searchingAll && !filteredData.length)}
            error={error}
            empty={!filteredData.length}
            what="Search is unavailable."
            emptyMessage={`No matches for “${query}”`}
            onRetry={retry}
            timeoutMs={SEARCH_TIMEOUT_MS + 3000}
          >
            {mine.length ? (
              <>
                <AdminSectionLabel className="px-3 pt-1">
                  {shellTeam?.name}
                </AdminSectionLabel>
                <MemberRows
                  members={mine}
                  showTeam={false}
                  onOpen={openMember}
                />
              </>
            ) : null}
            {others.length ? (
              <>
                {mine.length ? (
                  <AdminSectionLabel className="px-3">
                    Other teams
                  </AdminSectionLabel>
                ) : null}
                <MemberRows
                  members={others}
                  showTeam={!teamID}
                  onOpen={openMember}
                />
              </>
            ) : null}
            {hasNextPage ? (
              <div className="flex justify-center pb-3">
                <button
                  type="button"
                  disabled={isFetching}
                  onClick={() => fetchNextPage()}
                  className="min-h-11 rounded-lg bg-white/[0.08] px-4 text-[15px] text-white/90 active:bg-white/[0.12] disabled:opacity-50"
                >
                  {isFetching ? "Loading…" : "Show more"}
                </button>
              </div>
            ) : null}
            <SearchCoverage searchingAll={searchingAll} partial={partial} />
          </LoadState>
        )}
      </div>
    </div>
  );
};

/** A search result's member info, pushed over the page: Message, then the full profile. */
export const MemberScreen = () => {
  const memberView = useMemberView();
  if (!memberView.value) return null;
  return (
    <PushedScreen
      label="Member info"
      title="Member info"
      backLabel="Back to search"
      onBack={memberView.close}
    >
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]">
        <MemberInfoScreen key={memberView.value} userId={memberView.value} />
      </div>
    </PushedScreen>
  );
};

/**
 * Member search on a phone: a full-height sheet with one column of members,
 * each opening their member info. Tablets and desktop keep the palette.
 */
export const MemberSearchSheet = () => {
  const router = useRouter();
  const sheet = useSearchSheet();
  const open = sheet.value === "open";
  const input = useRef<HTMLInputElement>(null);

  return (
    <BottomSheet
      full
      urlView="searchSheet"
      open={open}
      onOpenChange={(next) => !next && sheet.close()}
      title="Search"
      initialFocus={input}
    >
      {open ? (
        <CommandCenterMembersSearchProvider
          initialState={{
            search:
              typeof router.query.searchQ === "string"
                ? router.query.searchQ
                : "",
            teamID:
              typeof router.query.searchTeam === "string"
                ? router.query.searchTeam
                : "",
          }}
        >
          <Results inputRef={input} />
        </CommandCenterMembersSearchProvider>
      ) : null}
    </BottomSheet>
  );
};
